/**
 * Staking UI.
 *
 * The copy here works hard to keep two things straight, because getting either
 * wrong costs users money:
 *
 *  - the plan rate is the return over the **whole term**, not per year, so the
 *    annualised figure is always labelled as an approximation and never shown
 *    on its own;
 *  - an early exit forfeits the reward but returns the principal in full, so
 *    the button says exactly that rather than "withdraw".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Address, Hex } from "viem";

import { browserPublicClient } from "@/lib/chain.client";
import { erc20Abi, stakingAbi } from "@/lib/contracts";
import { ASETQU_TOKEN, STAKING, TOKEN_SYMBOL, explorerTx } from "@/lib/config";
import { describeTxError } from "@/lib/errors";
import {
  AmountParseError,
  formatCountdown,
  formatDisplay,
  formatDuration,
  formatTimestamp,
  parseAmount,
} from "@/lib/format";
import { useWallet } from "./WalletProvider";
import { Alert, Button, Card, Stat } from "./ui";

import type { StakingPlan as Plan, StakingProgramme as Programme } from "@/lib/staking";

type PositionView = {
  positionId: string;
  planId: number;
  principalRaw: string;
  rewardRaw: string;
  startTime: string;
  unlockTime: string;
  earlyExitAllowed: boolean;
  withdrawn: boolean;
  mature: boolean;
};

type Positions = {
  positions: PositionView[];
  chainTimestamp?: string;
  truncated?: boolean;
  totalPositions?: number;
  chainError?: string;
};

async function fetchProgramme(): Promise<Programme | null> {
  try {
    const response = await fetch("/api/staking", { cache: "no-store" });
    return (await response.json()) as Programme;
  } catch {
    return null;
  }
}

async function fetchPositions(address: Address): Promise<Positions | null> {
  try {
    const response = await fetch(`/api/staking/positions/${address}`, { cache: "no-store" });
    return (await response.json()) as Positions;
  } catch {
    return null;
  }
}

/**
 * @param initialProgramme Rendered server-side, so plans and pool figures are
 * in the first paint rather than after a client round trip. Refreshed here
 * only after the user changes something.
 */
export function StakingPanel({
  initialProgramme = null,
}: {
  /** Null when the page had nothing to hand over, which is now every first
   *  load: the server no longer renders this page, so the panel fetches. */
  initialProgramme?: Programme | null;
}) {
  const { address, isCorrectNetwork } = useWallet();
  const [programme, setProgramme] = useState<Programme | null>(initialProgramme);
  const [reloadToken, setReloadToken] = useState(0);

  // Whether the mount-time load has been attempted. A ref rather than a
  // dependency on `programme`: depending on it would re-fetch the moment the
  // fetch succeeded, which is a loop.
  const loadedOnce = useRef(initialProgramme !== null);

  useEffect(() => {
    // Also on mount when nothing was handed over, which is the normal path now
    // that the page is rendered client-side.
    if (reloadToken === 0 && loadedOnce.current) return;
    loadedOnce.current = true;

    let cancelled = false;
    void fetchProgramme().then((data) => {
      if (!cancelled && data) setProgramme(data);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  if (!STAKING || (programme && !programme.configured)) {
    return (
      <Card title="Staking belum dibuka" className="transaction-card">
        <Alert tone="warn" title="Kontrak staking belum dikonfigurasi">
          Staking akan aktif setelah kontrak dideploy, diverifikasi, dan reward
          pool-nya didanai.
        </Alert>
      </Card>
    );
  }

  if (!programme || programme.chainError) {
    return (
      <Card title="Staking" className="transaction-card">
        <Alert tone="warn" title="Data on-chain tidak tersedia">
          Node RPC tidak dapat dihubungi saat ini. Muat ulang halaman untuk
          mencoba lagi.
        </Alert>
      </Card>
    );
  }

  const activePlans = programme.plans.filter((plan) => plan.active);

  return (
    <div className="staking-workspace">
      <Card title="Status program" className="ui-card--ledger">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat
            label="Total dikunci"
            value={`${formatDisplay(BigInt(programme.totalPrincipalRaw ?? "0"))} ${TOKEN_SYMBOL}`}
            hint="Principal seluruh staker"
          />
          <Stat
            label="Reward dicadangkan"
            value={`${formatDisplay(BigInt(programme.reservedRewardsRaw ?? "0"))} ${TOKEN_SYMBOL}`}
            hint="Sudah dikunci untuk posisi berjalan"
          />
          <Stat
            label="Sisa anggaran reward"
            value={`${formatDisplay(BigInt(programme.availableRewardsRaw ?? "0"))} ${TOKEN_SYMBOL}`}
            hint="Menentukan berapa banyak stake baru yang bisa diterima"
          />
        </div>

        {programme.solvent === false ? (
          <div className="mt-4">
            <Alert tone="danger" title="Kontrak melaporkan status tidak solven">
              Jangan melakukan stake baru dan laporkan ke kanal support resmi.
            </Alert>
          </div>
        ) : null}

        {programme.paused ? (
          <div className="mt-4">
            <Alert tone="warn" title="Stake baru sedang dijeda">
              Penarikan <strong>tidak</strong> terpengaruh. Posisi yang sudah
              berjalan tetap dapat ditarik sesuai jadwalnya.
            </Alert>
          </div>
        ) : null}
      </Card>

      <Card
        title="Plan yang tersedia"
        className="ui-card--plans"
        description="Reward dihitung dan dicadangkan saat Anda melakukan stake, bukan diakru seiring waktu."
      >
        {activePlans.length === 0 ? (
          <Alert tone="info">
            Belum ada plan yang dibuka. Parameter program masih menunggu
            penetapan resmi.
          </Alert>
        ) : (
          <div className="space-y-3">
            {activePlans.map((plan) => (
              <PlanRow key={plan.planId} plan={plan} />
            ))}
          </div>
        )}
      </Card>

      {address && isCorrectNetwork ? (
        <StakeForm
          key={address}
          address={address}
          plans={activePlans}
          paused={programme.paused === true}
          onChanged={reload}
        />
      ) : (
        <Card title="Stake" className="transaction-card">
          <Alert tone="info">
            {address
              ? "Pindah ke jaringan yang benar untuk melakukan stake."
              : "Hubungkan wallet untuk melakukan stake."}
          </Alert>
        </Card>
      )}

      {address && isCorrectNetwork ? (
        <PositionList key={`positions-${address}`} address={address} onChanged={reload} />
      ) : null}
    </div>
  );
}

function PlanRow({ plan }: { plan: Plan }) {
  return (
    <div className="plan-row">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold">
          Kunci {formatDuration(BigInt(plan.durationSeconds))}
        </p>
        <p className="text-lg font-semibold tabular-nums text-brand">
          {(plan.rewardRateBps / 100).toFixed(2)}%
          <span className="ml-1 text-xs font-normal text-ink-muted">per periode</span>
        </p>
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex justify-between gap-4">
          <dt className="text-ink-muted">Setara APR</dt>
          <dd className="tabular-nums">≈ {plan.approxApr.toFixed(1)}%</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-ink-muted">Minimum stake</dt>
          <dd className="tabular-nums">
            {formatDisplay(BigInt(plan.minStakeRaw))} {TOKEN_SYMBOL}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-ink-muted">Keluar lebih awal</dt>
          <dd>
            {plan.earlyExitAllowed ? (
              <span className="text-ok">Boleh, reward hangus</span>
            ) : (
              <span className="text-warn">Tidak boleh</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-ink-muted">Plan ID</dt>
          <dd className="font-mono">{plan.planId}</dd>
        </div>
      </dl>
    </div>
  );
}

type Phase = "idle" | "approving" | "staking" | "pending" | "error";

function StakeForm({
  address,
  plans,
  paused,
  onChanged,
}: {
  address: Address;
  plans: Plan[];
  paused: boolean;
  onChanged: () => void;
}) {
  const { getWalletClient } = useWallet();

  const [planId, setPlanId] = useState<number | null>(plans[0]?.planId ?? null);
  const [amountInput, setAmountInput] = useState("");
  const [balance, setBalance] = useState<bigint | null>(null);
  // The preview carries the inputs it was computed for, so a slow response for
  // an amount the user has already changed is ignored rather than shown.
  const [preview, setPreview] = useState<{
    planId: number;
    amount: bigint;
    reward: bigint;
    fundable: boolean;
  } | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<Hex | null>(null);

  useEffect(() => {
    if (!ASETQU_TOKEN) return;
    let cancelled = false;

    void browserPublicClient
      .readContract({
        address: ASETQU_TOKEN,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [address],
      })
      .then((value) => {
        if (!cancelled) setBalance(value);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [address, txHash]);

  const parsedAmount = useMemo(() => {
    try {
      return parseAmount(amountInput);
    } catch {
      return null;
    }
  }, [amountInput]);

  // Preview the reward from the contract itself rather than recomputing the
  // formula here — two implementations of the same maths is two chances to
  // show a number that does not match what the user will receive.
  useEffect(() => {
    if (!STAKING || planId === null || parsedAmount === null) return;

    let cancelled = false;
    void browserPublicClient
      .readContract({
        address: STAKING,
        abi: stakingAbi,
        functionName: "previewStake",
        args: [planId, parsedAmount],
      })
      .then(([reward, fundable]) => {
        if (!cancelled) setPreview({ planId, amount: parsedAmount, reward, fundable });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [planId, parsedAmount]);

  // Only trust a preview that still describes what is currently in the form.
  const currentPreview =
    preview !== null &&
    planId !== null &&
    parsedAmount !== null &&
    preview.planId === planId &&
    preview.amount === parsedAmount
      ? preview
      : null;

  const submit = useCallback(async () => {
    if (!STAKING || !ASETQU_TOKEN || planId === null) return;

    setMessage(null);
    let amount: bigint;
    try {
      amount = parseAmount(amountInput);
    } catch (error) {
      setPhase("error");
      setMessage(error instanceof AmountParseError ? error.message : "Jumlah tidak valid.");
      return;
    }

    if (balance !== null && amount > balance) {
      setPhase("error");
      setMessage("Jumlah melebihi saldo Anda.");
      return;
    }

    try {
      const walletClient = getWalletClient();

      const allowance = await browserPublicClient.readContract({
        address: ASETQU_TOKEN,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, STAKING],
      });

      // Approve exactly what is being staked. An unlimited allowance would
      // outlive the stake with no benefit to the user.
      if (allowance < amount) {
        setPhase("approving");
        const approveHash = await walletClient.writeContract({
          address: ASETQU_TOKEN,
          abi: erc20Abi,
          functionName: "approve",
          args: [STAKING, amount],
          account: address,
          chain: browserPublicClient.chain,
        });
        await browserPublicClient.waitForTransactionReceipt({ hash: approveHash });
      }

      // Simulate so a plan that just filled up, or a pool that just emptied,
      // becomes a readable message instead of a reverted transaction.
      const { request } = await browserPublicClient.simulateContract({
        address: STAKING,
        abi: stakingAbi,
        functionName: "stake",
        args: [planId, amount],
        account: address,
      });

      setPhase("staking");
      const hash = await walletClient.writeContract({ ...request, account: address });
      setTxHash(hash);

      setPhase("pending");
      const receipt = await browserPublicClient.waitForTransactionReceipt({ hash });

      if (receipt.status === "success") {
        setPhase("idle");
        setAmountInput("");
        onChanged();
      } else {
        setPhase("error");
        setMessage("Transaksi gagal di jaringan. Token Anda tidak berpindah.");
      }
    } catch (error) {
      setPhase("error");
      setMessage(describeError(error));
    }
  }, [address, amountInput, balance, getWalletClient, planId, onChanged]);

  if (plans.length === 0) return null;

  const busy = phase === "approving" || phase === "staking" || phase === "pending";
  const selectedPlan = plans.find((plan) => plan.planId === planId) ?? null;

  return (
    <Card title="Stake" className="transaction-card">
      <div className="space-y-4">
        {paused ? (
          <Alert tone="warn">Stake baru sedang dijeda oleh operator program.</Alert>
        ) : null}

        <div className="space-y-2">
          <label htmlFor="stake-plan" className="block text-sm font-medium">
            Plan
          </label>
          <select
            id="stake-plan"
            value={planId ?? ""}
            onChange={(event) => setPlanId(Number(event.target.value))}
            className="field-control"
          >
            {plans.map((plan) => (
              <option key={plan.planId} value={plan.planId}>
                {formatDuration(BigInt(plan.durationSeconds))} —{" "}
                {(plan.rewardRateBps / 100).toFixed(2)}% per periode
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label htmlFor="stake-amount" className="block text-sm font-medium">
            Jumlah {TOKEN_SYMBOL}
          </label>
          <input
            id="stake-amount"
            inputMode="decimal"
            autoComplete="off"
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
            placeholder="0.0"
            className="field-control font-mono"
          />
          <p className="text-xs text-ink-muted">
            Saldo: {balance === null ? "—" : `${formatDisplay(balance)} ${TOKEN_SYMBOL}`}
          </p>
        </div>

        {currentPreview ? (
          currentPreview.fundable ? (
            <Alert tone="info" title="Reward yang akan dikunci untuk Anda">
              {formatDisplay(currentPreview.reward)} {TOKEN_SYMBOL}, dibayarkan bersama
              principal setelah{" "}
              {selectedPlan
                ? formatDuration(BigInt(selectedPlan.durationSeconds))
                : "masa kunci"}
              . Jumlah ini dicadangkan dari pool saat transaksi Anda masuk dan
              tidak berubah setelahnya.
            </Alert>
          ) : (
            <Alert tone="warn" title="Sisa anggaran reward tidak mencukupi">
              Kurangi jumlah stake, atau tunggu pool ditambah. Kontrak menolak
              stake yang rewardnya belum bisa didanai — posisi yang sudah ada
              tidak pernah terpengaruh.
            </Alert>
          )
        ) : null}

        {txHash ? (
          <Alert tone={phase === "idle" ? "ok" : "info"}>
            <a
              href={explorerTx(txHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Lihat transaksi di explorer
            </a>
          </Alert>
        ) : null}

        {message ? <Alert tone="danger">{message}</Alert> : null}

        <Button
          onClick={() => void submit()}
          disabled={busy || paused || parsedAmount === null || currentPreview?.fundable === false}
        >
          {phase === "approving"
            ? "Menyetujui allowance…"
            : phase === "staking"
              ? "Menunggu tanda tangan…"
              : phase === "pending"
                ? "Menunggu konfirmasi…"
                : "Stake sekarang"}
        </Button>
      </div>
    </Card>
  );
}

function PositionList({ address, onChanged }: { address: Address; onChanged: () => void }) {
  const { getWalletClient } = useWallet();

  const [data, setData] = useState<Positions | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<Hex | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void fetchPositions(address).then((result) => {
      if (!cancelled) setData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [address, reloadToken]);

  const act = useCallback(
    async (positionId: string, early: boolean) => {
      if (!STAKING) return;
      setMessage(null);
      setBusyId(positionId);

      try {
        const walletClient = getWalletClient();
        const { request } = await browserPublicClient.simulateContract({
          address: STAKING,
          abi: stakingAbi,
          functionName: early ? "unstakeEarly" : "unstake",
          args: [BigInt(positionId)],
          account: address,
        });

        const hash = await walletClient.writeContract({ ...request, account: address });
        setTxHash(hash);

        const receipt = await browserPublicClient.waitForTransactionReceipt({ hash });
        if (receipt.status === "success") {
          setReloadToken((n) => n + 1);
          onChanged();
        } else {
          setMessage("Transaksi gagal di jaringan. Posisi Anda tidak berubah.");
        }
      } catch (error) {
        setMessage(describeError(error));
      } finally {
        setBusyId(null);
      }
    },
    [address, getWalletClient, onChanged],
  );

  const open = (data?.positions ?? []).filter((position) => !position.withdrawn);
  const closed = (data?.positions ?? []).filter((position) => position.withdrawn);
  const now = data?.chainTimestamp ? BigInt(data.chainTimestamp) : null;

  return (
    <Card title="Posisi Anda" className="ui-card--positions">
      {data?.chainError ? (
        <Alert tone="warn">Data posisi tidak dapat dibaca dari jaringan saat ini.</Alert>
      ) : null}

      {data === null ? (
        <div className="loading-state" role="status" aria-label="Memuat posisi">
          <span />
          <span />
          <span />
        </div>
      ) : open.length === 0 && closed.length === 0 ? (
        <p className="text-sm text-ink-muted">Belum ada posisi staking.</p>
      ) : (
        <div className="space-y-3">
          {data.truncated ? (
            <Alert tone="info">
              Menampilkan {data.positions.length} dari {data.totalPositions} posisi.
            </Alert>
          ) : null}

          {open.map((position) => (
            <div
              key={position.positionId}
              className="position-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold tabular-nums">
                  {formatDisplay(BigInt(position.principalRaw))} {TOKEN_SYMBOL}
                </p>
                <p className="text-sm text-ok tabular-nums">
                  + {formatDisplay(BigInt(position.rewardRaw))} {TOKEN_SYMBOL} reward
                </p>
              </div>

              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-ink-muted">Jatuh tempo</dt>
                  <dd className="text-right">
                    {formatTimestamp(BigInt(position.unlockTime))} WIB
                    {now ? (
                      <span className="block text-xs text-ink-muted">
                        {formatCountdown(BigInt(position.unlockTime), now)}
                      </span>
                    ) : null}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-ink-muted">Posisi</dt>
                  <dd className="font-mono">#{position.positionId}</dd>
                </div>
              </dl>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={() => void act(position.positionId, false)}
                  disabled={!position.mature || busyId === position.positionId}
                >
                  {busyId === position.positionId
                    ? "Memproses…"
                    : position.mature
                      ? "Tarik principal + reward"
                      : "Belum jatuh tempo"}
                </Button>

                {!position.mature && position.earlyExitAllowed ? (
                  <Button
                    variant="secondary"
                    onClick={() => void act(position.positionId, true)}
                    disabled={busyId === position.positionId}
                  >
                    Tarik awal — principal utuh, reward hangus
                  </Button>
                ) : null}
              </div>
            </div>
          ))}

          {closed.length > 0 ? (
            <details className="position-history">
              <summary className="cursor-pointer text-sm text-ink-muted">
                {closed.length} posisi yang sudah ditarik
              </summary>
              <ul className="mt-3 space-y-1 text-sm text-ink-muted">
                {closed.map((position) => (
                  <li key={position.positionId} className="flex justify-between gap-4">
                    <span className="font-mono">#{position.positionId}</span>
                    <span className="tabular-nums">
                      {formatDisplay(BigInt(position.principalRaw))} {TOKEN_SYMBOL}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}

      {txHash ? (
        <div className="mt-4">
          <Alert tone="ok">
            <a
              href={explorerTx(txHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Lihat transaksi di explorer
            </a>
          </Alert>
        </div>
      ) : null}

      {message ? (
        <div className="mt-4">
          <Alert tone="danger">{message}</Alert>
        </div>
      ) : null}
    </Card>
  );
}

/** Kept as a local alias so call sites read the same as before; the mapping
 *  itself is shared with the claim flow in `lib/errors.ts`. */
const describeError = describeTxError;
